import SelectDamagedPartGroup from 'components/elements/form/DamagedPartGroup/SelectDamagedPartGroup'
import RadioButtonGroup from 'components/elements/form/RadioButtonGroup'
import SelectDestinationContent from 'components/elements/items/SelectDestinationContent'
import KsSelectDestinationContent from 'components/elements/items/KsSelectDestinationContent'
import SelectVehicleModificationContent from 'components/elements/items/SelectVehicleModificationContent'
import RequestTemplate from 'components/templates/RequestTemplate'
import { WARNING } from 'constants/messages'
import { PAGE_PATH } from 'constants/pagePath'
import React, { useEffect } from 'react'
import { VEHICLE_STATE_FORM } from 'constants/qnState'
import {
  FlatTireCauseTypes,
  AccidentPartInfo,
  SpareTireStatusTypes,
  DestinationTypes,
  QnStatesProps,
  initialQnEditState,
  VehicleHeightStatusTypes,
} from 'states/QnState'
import {
  StatesProps,
  useAddressMatch,
  useEditQnState,
  useProgressPoint,
} from 'utils/commonHooks'
import { isSjDomain, isKsDomain, isSaficDomain, getDomain } from 'utils/domain'
import SwitchButtonGroup from 'components/elements/form/SwitchButtonGroup'
import { createOptions } from 'utils/commonUtils'
import { success } from 'utils/status'
import KsSelectVehicleModificationContent from 'components/elements/items/KsSelectVehicleModificationContent'
import DOMAIN from 'constants/domains'

const spareTireStatusOptions = createOptions(
  isKsDomain()
    ? VEHICLE_STATE_FORM.spareTireKsStatus.options
    : VEHICLE_STATE_FORM.spareTireStatus.options
)

const flatTireCauseOptions = createOptions(
  isKsDomain()
    ? VEHICLE_STATE_FORM.flatTireKsCause.options
    : VEHICLE_STATE_FORM.flatTireCause.options
)

/**
 * QN-03-c: パンク
 */
const FlatTire: React.FC<StatesProps> = (props: QnStatesProps) => {
  const [editState, setEditingItem, setVehicleStateInfo] = useEditQnState()
  const { state, setState } = props
  const {
    destinationType,
    isNotApplicableParts,
    spareTireStatus,
    accidentPart,
    isMotorbike,
    flatTireCause,
    vehicleHeightStatus,
  } = editState.vehicleStateInfo

  /** 配送先とトラブル場所のバリデーション */
  const addressMatchError = useAddressMatch(editState)

  // 配送先指定可能時のバリデーション
  const destinationValidate = (): boolean | undefined => {
    if (
      // スペアタイヤあり以外選択維持かつ運び先指定を選択時
      !spareTireStatus.includes(SpareTireStatusTypes.yes) &&
      destinationType.includes(DestinationTypes.specify)
    ) {
      if (!editState.destinationLocation.isSelected) return true
      if (editState.destinationLocation.isSelected) return addressMatchError
      return true
    }
  }

  // バリデーション
  const isDisabled = React.useMemo(() => {
    if (accidentPart.parts.length === 0) {
      if (isNotApplicableParts) {
        return destinationValidate()
      }
      return !isNotApplicableParts
    }
    return destinationValidate()
  }, [
    editState.vehicleStateInfo,
    editState.destinationLocation,
    addressMatchError,
  ])

  // 警告メッセージ
  const warning = React.useMemo(() => {
    if (spareTireStatus.includes(SpareTireStatusTypes.yes)) {
      return ''
    }
    switch (destinationType) {
      case DestinationTypes.specify:
        return isKsDomain() ? WARNING.WRECKER_KS : WARNING.WRECKER
      case DestinationTypes.introduction:
        if (
          // 共済系ドメイン時 かつ「入庫先紹介希望」選択時
          isKsDomain()
        ) {
          return ''
        }
        return WARNING.INTRODUCTION
    }
  }, [destinationType, spareTireStatus])

  // 決定押下時の遷移先
  const forwardTo = React.useMemo(() => {
    const transitionDestination = isKsDomain()
      ? PAGE_PATH.CONTRACT_INFORMATION
      : isSaficDomain()
      ? PAGE_PATH.SERVICE_INFORMATION
      : {
          [DOMAIN.SJ]: PAGE_PATH.CONTRACT_INFORMATION,
        }[getDomain()]
    return state.confirmationChangeType
      ? PAGE_PATH.CONFIRMATION
      : transitionDestination
  }, [state.confirmationChangeType, getDomain()])

  /** 破損箇所情報変更時のコールバック */
  const handleDamagedPartChanged = React.useCallback(
    (value: AccidentPartInfo): void => {
      setVehicleStateInfo({
        accidentPart: value,
      })
    },
    [setVehicleStateInfo]
  )

  /** バイクでのパンクトラブルスイッチ変更時のコールバック */
  const handleIsMotorbikeChanged = React.useCallback(
    (value: boolean) => {
      setVehicleStateInfo({
        isMotorbike: value,
        accidentPart: {
          ...initialQnEditState.vehicleStateInfo.accidentPart,
        },
      })
    },
    [setVehicleStateInfo]
  )

  /** タイヤ軸が複数ある車両トラブルのスイッチ変更時のコールバック */
  const handleIsNotApplicablePartsChanged = React.useCallback(
    (value: boolean) => {
      setVehicleStateInfo({
        isNotApplicableParts: value,
      })
    },
    [setVehicleStateInfo]
  )

  /** スペアタイヤの状態選択変更時のコールバック */
  const handleSpareTireStatusChanged = React.useCallback(
    (value: SpareTireStatusTypes) => {
      setVehicleStateInfo({
        spareTireStatus: value,
      })
    },
    [setVehicleStateInfo]
  )

  /** パンク原因の選択変更時のコールバック */
  const handleFlatTireCauseChanged = React.useCallback(
    (value: FlatTireCauseTypes) => {
      setVehicleStateInfo({
        flatTireCause: value,
      })
    },
    [setVehicleStateInfo]
  )

  const forwardAction = React.useCallback(() => {
    setState({
      ...state,
      troubleDatetime: editState.troubleDatetime,
      vehicleState: editState.vehicleState ?? '',
      vehicleStateInfo: editState.vehicleStateInfo,
      destinationLocation: editState.destinationLocation,
    })
    return success()
  }, [
    editState.vehicleState,
    editState.vehicleStateInfo,
    editState.troubleDatetime,
    editState.destinationLocation,
  ])

  /** 戻るボタン押下時にeditingItemの中身をqnStateの内容と同期させる */
  const backAction = React.useCallback(() => {
    setEditingItem({
      ...editState,
      vehicleStateInfo: state.vehicleStateInfo,
      troubleDatetime: state.troubleDatetime,
      vehicleState: state.vehicleState,
      destinationLocation: state.destinationLocation,
    })
    return success()
  }, [
    state.confirmationChangeType,
    state.vehicleStateInfo,
    state.troubleDatetime,
    state.vehicleState,
    state.destinationLocation,
  ])

  // EditingItemに "初期値" または "すでに選択済みのもの" をセットする
  useEffect(() => {
    setVehicleStateInfo({
      flatTireCause:
        flatTireCause === '' ? FlatTireCauseTypes.accident : flatTireCause,
      spareTireStatus:
        spareTireStatus === '' ? SpareTireStatusTypes.yes : spareTireStatus,
      destinationType:
        destinationType === '' ? DestinationTypes.specify : destinationType,
      vehicleHeightStatus:
        vehicleHeightStatus === ''
          ? VehicleHeightStatusTypes.moreThan9cm
          : vehicleHeightStatus,
    })
  }, [])

  return (
    <RequestTemplate
      title={'パンク'}
      showContact
      progress={useProgressPoint()}
      description={
        '作業のための装備、レッカーの必要性などを判断するため、以下の情報を教えてください。'
      }
      content={
        <>
          <SelectDamagedPartGroup
            title={`${VEHICLE_STATE_FORM.flatTirePart.formName}を選択（複数選択可）`}
            isRequired={!isNotApplicableParts}
            damagedStatus={{
              ...accidentPart,
            }}
            isMotorbike={isMotorbike}
            onChanged={(value: AccidentPartInfo): void => {
              handleDamagedPartChanged(value)
            }}
          />
          {(isSjDomain() || isKsDomain()) && (
            <>
              <SwitchButtonGroup
                title={`${VEHICLE_STATE_FORM.motorbikeFlatTire.formName}`}
                isSwitched={isMotorbike}
                label={`${VEHICLE_STATE_FORM.motorbikeFlatTire.label}`}
                onChanged={handleIsMotorbikeChanged}
              />
              <SwitchButtonGroup
                title={`${VEHICLE_STATE_FORM.notApplicableParts.formName}`}
                isSwitched={isNotApplicableParts}
                label={`${VEHICLE_STATE_FORM.notApplicableParts.label}`}
                onChanged={handleIsNotApplicablePartsChanged}
              />
            </>
          )}
          <RadioButtonGroup
            title={`${
              isKsDomain()
                ? VEHICLE_STATE_FORM.spareTireKsStatus.formName
                : VEHICLE_STATE_FORM.spareTireStatus.formName
            }を選択`}
            isRequired
            options={spareTireStatusOptions}
            selectedValue={spareTireStatus}
            onValueChanged={handleSpareTireStatusChanged}
          />
          <RadioButtonGroup
            title={`${
              isKsDomain()
                ? VEHICLE_STATE_FORM.flatTireKsCause.formName
                : VEHICLE_STATE_FORM.flatTireCause.formName
            }を選択`}
            isRequired
            options={flatTireCauseOptions}
            selectedValue={flatTireCause}
            onValueChanged={handleFlatTireCauseChanged}
          />
          {isKsDomain() ? (
            <KsSelectVehicleModificationContent />
          ) : (
            <SelectVehicleModificationContent />
          )}
          {!spareTireStatus.includes(SpareTireStatusTypes.yes) && (
            <>
              {isKsDomain() ? (
                <KsSelectDestinationContent isIntroduction={true} />
              ) : (
                <SelectDestinationContent />
              )}
            </>
          )}
        </>
      }
      warning={warning}
      forwardTo={forwardTo}
      showBack
      forwardAction={forwardAction}
      backAction={backAction}
      backTo={PAGE_PATH.VEHICLE_STATE}
      isDisabled={isDisabled}
      notClearEditingItem
    />
  )
}

export default FlatTire
